import React, { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./useAuth";
import { getUserWishlistV2 } from "./useWishlist";

interface WishlistContextType {
  wishlistedIds: number[];
  isLoading: boolean;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(
  undefined
);

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [wishlistedIds, setWishlistedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Start as false to not block UI

  const fetchWishlistIds = async () => {
    if (!user?.id) {
      setWishlistedIds([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
  

      // Add timeout to prevent long waits
      const timeoutPromise = new Promise(
        (_, reject) =>
          setTimeout(() => reject(new Error("Wishlist fetch timeout")), 5000) // Reduced to 5 seconds
      );

      const fetchPromise = getUserWishlistV2({
        user_id: user.id,
        limit: 25, // Further reduced for better performance
        offset: 0,
        locale: "en",
      });

      const response = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as any;


      if (
        response &&
        response.status === "success" &&
        response.data &&
        response.data.items
      ) {
        // Extract just the product IDs
        const ids = response.data.items.map((item: any) => item.id);
        setWishlistedIds(ids);
      } else {
        setWishlistedIds([]);
      }
    } catch (error) {

      setWishlistedIds([]);
      // Don't show error to user for wishlist - just continue without it
    } finally {
      setIsLoading(false);
    }
  };

  const refreshWishlist = async () => {
    await fetchWishlistIds();
  };

  useEffect(() => {
    // Don't block the UI - fetch wishlist data in the background
    const timer = setTimeout(() => {
      fetchWishlistIds();
    }, 100); // Small delay to let the page render first

    return () => clearTimeout(timer);
  }, [user?.id]);

  const value: WishlistContextType = {
    wishlistedIds,
    isLoading,
    refreshWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlistContext = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error(
      "useWishlistContext must be used within a WishlistProvider"
    );
  }
  return context;
};
